import { FC } from 'react'
import { FiClock, FiCalendar, FiUser } from 'react-icons/fi'
import Pagination from '../../Pagination'
import { useTranslation } from 'react-i18next'
import { DotsLoading } from '../../Loading'
import EmptyTicket from '../../TicketStatusCard/EmptyTicket'
import ComponentHeader from '../ComponentHeader/ComponentHeader'
import PanelSurface from '../PanelSurface/PanelSurface'
import styles from './ListMemberPutNumber.module.css'

export interface PutNumberEntry {
  rank: number
  number: string
  username: string
  dateTime: string
}

interface ListMemberPutNumberProps {
  entries: PutNumberEntry[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  title?: string
  isLoading?: boolean
}

const ListMemberPutNumber: FC<ListMemberPutNumberProps> = ({
  entries,
  currentPage,
  totalPages,
  onPageChange,
  title,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  return (
    <PanelSurface className={styles.listMemberPutNumber}>
      <ComponentHeader>
        {title || t('yeekeeResult.listMemberPutNumber')}
      </ComponentHeader>

      <div className="px-4 md:px-8 py-4 md:py-6 space-y-2">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <DotsLoading size="sm" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <EmptyTicket />
          </div>
        ) : (
          <div className="list-member-table-container">
            {/* Table Headers - Only visible in Blue theme */}
            <div className="list-member-table-headers">
              <div className="text-sm font-medium text-[var(--caption-text)]">
                {t('yeekeeResult.listMemberTableHeaders.rank')}
              </div>
              <div className="text-sm font-medium text-[var(--caption-text)]">
                {t('yeekeeResult.listMemberTableHeaders.shootNumber')}
              </div>
              <div className="text-sm font-medium text-[var(--caption-text)]">
                {t('yeekeeResult.listMemberTableHeaders.username')}
              </div>
              <div className="text-sm font-medium text-[var(--caption-text)]">
                {t('yeekeeResult.listMemberTableHeaders.betDate')}
              </div>
            </div>

            {/* Table Rows */}
            {entries.map((entry) => (
              <div
                key={entry.rank}
                className={`list-member-table-row ${
                  entry.rank === 1 || entry.rank === 16 ? 'selected' : ''
                }`}
              >
                {/* Rank Column */}
                <div className="flex items-center">
                  <span
                    className={`px-3 py-1.5 rounded-lg text-sm min-w-[50px] text-center flex-shrink-0 justify-center items-center flex ${
                      entry.rank === 1 || entry.rank === 16
                        ? 'active-gradient text-white font-bold'
                        : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)]'
                    }`}
                  >
                    {entry.rank}
                  </span>
                </div>

                {/* Ticket Number Column */}
                <div className="flex items-center">
                  <span
                    className={`text-lg font-medium text-[var(--bet-type-hover-text)] ${
                      entry.rank === 1 || entry.rank === 16
                        ? 'ticket-number'
                        : ''
                    }`}
                  >
                    {entry.number}
                  </span>
                </div>

                {/* Username Column */}
                <div className="flex items-center">
                  <span className="text-sm text-[var(--bet-type-hover-text)]">
                    {entry.username}
                  </span>
                </div>

                {/* Bet Date Column */}
                <div className="flex items-center">
                  <span className="text-sm text-[var(--card-subtext)]">
                    {entry.dateTime.split(' ').slice(0, 3).join(' ')}{' '}
                    {entry.dateTime.split(' ')[3]}
                  </span>
                </div>
              </div>
            ))}

            {/* Fallback Layout for Gold Theme */}
            <div className="space-y-2">
              {entries.map((entry) => (
                <div
                  key={entry.rank}
                  className="flex items-center justify-between py-2 border-b border-[var(--panel-border)] last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1.5 rounded-lg text-sm min-w-[70px] text-center flex-shrink-0 justify-center items-center flex ${
                        entry.rank === 1 || entry.rank === 16
                          ? 'active-gradient text-[var(--pagination-text-active)] font-bold'
                          : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)]'
                      }`}
                    >
                      {t('yeekeeResult.rank', { rank: entry.rank })}
                    </span>
                    <span className="text-[var(--bet-type-hover-text)] text-lg font-medium min-w-[50px] text-left">
                      {entry.number}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full active-gradient">
                        <FiUser className="text-base" />
                      </span>
                      <span className="text-[var(--bet-type-hover-text)]">
                        {entry.username}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[var(--card-subtext)] text-xs">
                      <FiCalendar className="w-3 h-3" />
                      <span>
                        {entry.dateTime.split(' ').slice(0, 3).join(' ')}
                      </span>
                      <FiClock className="w-3 h-3" />
                      <span>{entry.dateTime.split(' ')[3]}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="px-4 md:px-8 pb-4 md:pb-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </PanelSurface>
  )
}

export default ListMemberPutNumber
